import { Component, inject, signal, OnDestroy, OnInit } from '@angular/core';
import { RouterOutlet, Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { LoadingService } from './core/services/loading.service';
import { resetBodyUiLocks } from './core/utils/ui-lock.util';
import { ProgressBarComponent } from './shared/components/progress-bar.component';
import { CookieConsentBannerComponent } from './shared/components/cookie-consent-banner/cookie-consent-banner.component';
import { TranslateService } from '@ngx-translate/core';
import {
  fetchVisitorCountryCode,
  isAppLang,
  langFromCountryCode,
  resolveInitialAppLang,
} from './core/utils/locale.util';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ProgressBarComponent, CookieConsentBannerComponent],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('krb');
  private router = inject(Router);
  private loadingService = inject(LoadingService);
  private translate = inject(TranslateService);
  private lastVisitedUrl = '/';

  constructor() {
    this.translate.addLangs?.(['fr', 'en']);
    this.translate.setDefaultLang?.('fr');
    const savedLang = typeof localStorage !== 'undefined' ? localStorage.getItem('lang') : null;
    const initialLang = resolveInitialAppLang();
    this.translate.use(initialLang);

    if (!isAppLang(savedLang)) {
      void this.refineLangFromVisitorCountry();
    }
  }

  private async refineLangFromVisitorCountry(): Promise<void> {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('lang_source') === 'user') {
      return;
    }

    const country = await fetchVisitorCountryCode();
    const lang = langFromCountryCode(country);
    if (!lang) {
      return;
    }

    if (this.translate.currentLang !== lang) {
      this.translate.use(lang);
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('lang', lang);
        localStorage.setItem('lang_source', 'geo');
      }
    } else if (typeof localStorage !== 'undefined' && !localStorage.getItem('lang')) {
      localStorage.setItem('lang', lang);
      localStorage.setItem('lang_source', 'geo');
    }
  }

  private readonly handleOffline = () => {
    this.router.navigate(['/offline']);
  };

  private readonly handleOnline = () => {
    if (this.router.url === '/offline') {
      this.router.navigateByUrl(this.lastVisitedUrl || '/');
    }
  };

  ngOnInit() {
    if (!navigator.onLine) {
      this.router.navigate(['/offline']);
    }

    window.addEventListener('offline', this.handleOffline);
    window.addEventListener('online', this.handleOnline);

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.loadingService.start();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.loadingService.complete();
      }

      if (event instanceof NavigationEnd) {
        if (event.urlAfterRedirects.startsWith('/auth')) {
          resetBodyUiLocks();
        }

        if (!['/offline', '/404', '/500'].includes(event.urlAfterRedirects)) {
          this.lastVisitedUrl = event.urlAfterRedirects || '/';
        }
      }
    });
  }

  ngOnDestroy() {
    window.removeEventListener('offline', this.handleOffline);
    window.removeEventListener('online', this.handleOnline);
  }
}
